import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import ProgressBar from '../components/ProgressBar';
import logoHeader from '../assets/logo_header.png';
import axios from 'axios';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAFJhj2k-nTTUaJLT6gSaZm_m8wczl5k48",
  authDomain: "sja-payment-tracker.firebaseapp.com",
  projectId: "sja-payment-tracker",
  storageBucket: "sja-payment-tracker.appspot.com",
  messagingSenderId: "515297014604",
  appId: "1:515297014604:web:89d5b4b5a6b5c2b5b5b5b5"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

interface FormData {
  fullName: string;
  email: string;
  whatsapp: string;
  selectedClass: string;
}

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentLinkId, setPaymentLinkId] = useState('');
  const [partnerTxId, setPartnerTxId] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  
  // Get form data from location state or localStorage
  const formData: FormData = location.state || (() => {
    const savedData = localStorage.getItem('sjaFormData');
    return savedData ? JSON.parse(savedData) : null;
  })();

  const classes = [
    { 
      title: '2x Salary in 3 Years PLUS 5x Salary in 10 years',
      price: 'Rp1.950.000'
    },
    { 
      title: 'Penetrate Executive Status & Triple Digit Salary',
      price: 'Rp2.950.000'
    }
  ];

  useEffect(() => {
    // Redirect to checkout if no form data
    if (!formData) {
      navigate('/checkout');
      return;
    }

    // Auto-start payment process
    initializePayment();
  }, []);

  const initializePayment = async () => {
    setIsLoading(true);

    try {
      const selectedCourse = classes.find(course => course.title === formData.selectedClass);
      if (!selectedCourse) {
        throw new Error('Selected course not found');
      }

      // Check for existing payment URL in sessionStorage for the selected course
      const storedPayments = sessionStorage.getItem('sjaPaymentData');
      if (storedPayments) {
        const payments = JSON.parse(storedPayments);
        const coursePayment = payments[formData.selectedClass];
        
        if (coursePayment) {
          const hoursSinceCreation = (Date.now() - coursePayment.timestamp) / (1000 * 60 * 60);
          
          // If payment URL is less than 24 hours old, use it
          if (hoursSinceCreation < 24) {
            console.log('Using stored payment URL for course:', formData.selectedClass);
            setPaymentLinkId(coursePayment.paymentLinkId);
            setPartnerTxId(coursePayment.partnerTxId);
            setPaymentUrl(coursePayment.paymentUrl);
            setShowPaymentModal(true);
            monitorPaymentStatus(coursePayment.partnerTxId);
            setIsLoading(false);
            return;
          }
        }
      }

      // Generate partner_tx_id
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const generatedPartnerTxId = `SJA${timestamp}${random}`;

      console.log('Creating payment for course:', selectedCourse.title);
      const paymentPayload = {
        amount: parseInt(selectedCourse.price.replace(/[^0-9]/g, '')),
        email: formData.email,
        phone_number: formData.whatsapp,
        description: selectedCourse.title,
        name: formData.fullName,
        partner_tx_id: generatedPartnerTxId
      };
      
      console.log('Payment payload:', paymentPayload);
      
      const response = await axios.post('https://shaundjuacademy.com/api/create-payment', paymentPayload);

      const data = response.data;
      console.log('Payment API Response:', data);
      console.log('Response structure check:', {
        hasSuccess: !!data.success,
        hasPaymentLinkId: !!data.paymentLinkId,
        hasUrl: !!data.url,
        isExistingPayment: data.isExistingPayment
      });

      if (data && data.success && data.paymentLinkId) {
        setPaymentLinkId(data.paymentLinkId);
        setPartnerTxId(generatedPartnerTxId);
        
        // Store payment data in sessionStorage
        const storedPayments = sessionStorage.getItem('sjaPaymentData');
        const payments = storedPayments ? JSON.parse(storedPayments) : {};
        
        payments[formData.selectedClass] = {
          paymentLinkId: data.paymentLinkId,
          partnerTxId: generatedPartnerTxId,
          paymentUrl: data.url,
          timestamp: Date.now()
        };
        
        sessionStorage.setItem('sjaPaymentData', JSON.stringify(payments));
        
        handlePaymentWindow(data.paymentLinkId, generatedPartnerTxId, data.url);
      } else {
        throw new Error(data.error || data.message || 'Failed to create payment');
      }
    } catch (error) {
      console.error('Payment initialization failed:', error);
      alert('Failed to initialize payment. Please try again.');
      navigate('/checkout');
    }
  };

  const handlePaymentWindow = (linkId: string, txId: string, paymentUrlFromResponse?: string) => {
    setIsLoading(false);
    // Use the URL from OY response, or fallback to constructed URL
    const finalPaymentUrl = paymentUrlFromResponse || `https://pay.oyindonesia.com/${linkId}`;
    setPaymentUrl(finalPaymentUrl);
    setShowPaymentModal(true);
    
    // Start monitoring payment status
    monitorPaymentStatus(txId);
  };

  const monitorPaymentStatus = (txId: string) => {
    const unsubscribe = onSnapshot(doc(db, 'payments', txId), (doc) => {
      if (doc.exists()) {
        const paymentData = doc.data();
        console.log('Payment status update:', paymentData);
        
        if (paymentData.status === 'SUCCESS') {
          // Clear payment data on successful payment
          const storedPayments = sessionStorage.getItem('sjaPaymentData');
          if (storedPayments) {
            const payments = JSON.parse(storedPayments);
            delete payments[formData.selectedClass];
            sessionStorage.setItem('sjaPaymentData', JSON.stringify(payments));
          }
          unsubscribe(); // Clean up the Firestore listener
          navigate('/payment-success');
        } else if (paymentData.status === 'FAILED' || paymentData.status === 'EXPIRED') {
          // Clear payment data on failed payment
          const storedPayments = sessionStorage.getItem('sjaPaymentData');
          if (storedPayments) {
            const payments = JSON.parse(storedPayments);
            delete payments[formData.selectedClass];
            sessionStorage.setItem('sjaPaymentData', JSON.stringify(payments));
          }
          unsubscribe(); // Clean up the Firestore listener
          navigate('/payment-failed');
        }
      }
    });

    // Store unsubscribe function to clean up if needed
    (window as any).paymentUnsubscribe = unsubscribe;
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    navigate('/checkout');
  };

  if (!formData) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-[#FCFAF7]">
      {/* Progress Bar */}
      <ProgressBar currentStep={2} />
      
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <img 
            src={logoHeader} 
            alt="Shaun Dju Academy" 
            className="h-10 md:h-12"
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-[#203D32] mb-6"></div>
            <h2 className="text-2xl font-semibold text-[#203D32] mb-4">
              Redirecting to Secure Payment Gateway
            </h2>
            <p className="text-gray-600">
              Please wait while we prepare your payment...
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-center text-[#203D32] mb-8">
              Complete Your Payment
            </h1>
            
            <div className="max-w-md mx-auto">
              <div className="border rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-lg mb-2">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Name:</span>
                    <span>{formData.fullName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Email:</span>
                    <span>{formData.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>WhatsApp:</span>
                    <span>{formData.whatsapp}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span>Course:</span>
                    <span>{formData.selectedClass}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-[#203D32]">
                    <span>Total:</span>
                    <span>{classes.find(c => c.title === formData.selectedClass)?.price}</span>
                  </div>
                </div>
              </div>
              
              <button 
                onClick={() => setShowPaymentModal(true)}
                className="w-full bg-[#203D32] text-white text-lg px-6 py-4 rounded-full font-semibold hover:opacity-90 transition"
              >
                Continue to Payment
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Secure Payment through OY Indonesia</h3>
              <button 
                onClick={closePaymentModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="flex-1">
              <iframe 
                src={paymentUrl}
                className="w-full h-full border-0"
                title="Payment Gateway"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;
